import { plainDateOrNull, Temporal, toInstant, toPlainDate } from './temporal.js';
const MIDNIGHT = Temporal.PlainTime.from('00:00');
function isOpenEnd(bound) {
    return bound === null || bound === undefined;
}
export function isCalendarDate(value) {
    return typeof value === 'string' && plainDateOrNull(value) !== null;
}
/** A calendar date a Postgres `DATE` can hold: Postgres has no year 0, so `0000-…` is refused. */
export function isStorableCalendarDate(value) {
    return isCalendarDate(value) && !value.startsWith('0000-');
}
/** Throws a `RangeError` unless `value` is a real `YYYY-MM-DD` date. */
export function parseCalendarDate(value) {
    return toPlainDate(value).toString();
}
/** UTC midnight, the shape Prisma reads and writes for a `@db.Date` column. */
export function toPrismaDate(ymd) {
    return new Date(toPlainDate(ymd).toZonedDateTime('UTC').epochMilliseconds);
}
/** Throws a `RangeError` for anything but UTC midnight, so a wrong writer fails loudly. */
export function fromPrismaDate(date) {
    const utc = toInstant(date).toZonedDateTimeISO('UTC');
    if (!utc.toPlainTime().equals(MIDNIGHT)) {
        throw new RangeError(`Not a UTC-midnight @db.Date value: ${date.toISOString()}`);
    }
    return parseCalendarDate(utc.toPlainDate().toString());
}
/**
 * The UTC day of a calendar date still stored as a `timestamptz` instant (a picked `payment_date`
 * at UTC midnight, or a planned day at Pacific midnight), which is the day that was picked.
 *
 * Transitional, and lenient on purpose (any instant truncates to its UTC day): use it only for a
 * picked or planned day held in a `timestamptz` until F1 / F2b retype the column, or for a `Date`
 * built to be read by its UTC fields (`businessWallClockAsUtc`). Never for a `@db.Date` value
 * (`fromPrismaDate`, which refuses a wrong writer) and never for a real instant
 * (`businessDateOfTimestamp`, its Pacific day).
 */
export function calendarDateOfUtcInstant(date) {
    return parseCalendarDate(toInstant(date).toZonedDateTimeISO('UTC').toPlainDate().toString());
}
// One formatter per style: building one per call, as PlainDate.toLocaleString does, made a call
// about 8x slower end to end (29 us vs 4 us), and a grid formats every row. Reading the UTC-midnight
// Date in UTC keeps the day zone-free.
const calendarDateFormats = new Map();
function calendarDateFormat(month, withYear, withWeekday) {
    const key = `${month}:${withYear}:${withWeekday}`;
    const cached = calendarDateFormats.get(key);
    if (cached)
        return cached;
    const format = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        ...(withWeekday ? { weekday: 'long' } : {}),
        month,
        day: 'numeric',
        ...(withYear ? { year: 'numeric' } : {}),
    });
    calendarDateFormats.set(key, format);
    return format;
}
/** "Oct 13, 2026": the date as written, with no zone and no conversion. */
export function formatCalendarDate(ymd, { month = 'short', withYear = true, withWeekday = false } = {}) {
    return calendarDateFormat(month, withYear, withWeekday).format(toPrismaDate(ymd));
}
export function addDays(ymd, days) {
    return parseCalendarDate(toPlainDate(ymd).add({ days }).toString());
}
/** `ymd` plus whole calendar months, clamped to the target month's end (Aug 31 + 6 months is Feb 28/29). */
export function addMonths(ymd, months) {
    return parseCalendarDate(toPlainDate(ymd).add({ months }).toString());
}
/** Whole days from `start` to `end` (`end` minus `start`). */
export function daysBetween(start, end) {
    return toPlainDate(start).until(toPlainDate(end), { largestUnit: 'days' }).days;
}
/** Inclusive at both ends; a null or undefined end is open. */
export function isWithin(ymd, start, end) {
    const day = toPlainDate(ymd);
    const afterStart = isOpenEnd(start) || Temporal.PlainDate.compare(toPlainDate(start), day) <= 0;
    const beforeEnd = isOpenEnd(end) || Temporal.PlainDate.compare(day, toPlainDate(end)) <= 0;
    return afterStart && beforeEnd;
}
export function startOfMonth(ymd) {
    return parseCalendarDate(toPlainDate(ymd).with({ day: 1 }).toString());
}
//# sourceMappingURL=calendar-date.js.map